import { extendTheme, theme as baseTheme, ThemeConfig } from '@chakra-ui/react';
import { oceanColors, orchidColors, neonColors, businessColors, semanticColors, grayScale } from './foundations/colors';

// Theme configuration
const config: ThemeConfig = {
  initialColorMode: 'light',
  useSystemColorMode: true,
};

// Create Ocean theme
export const oceanTheme = extendTheme({
  config,
  colors: {
    primary: oceanColors.primary,
    secondary: oceanColors.secondary,
    accent: oceanColors.accent,
    success: semanticColors.success,
    warning: semanticColors.warning,
    error: semanticColors.error,
    info: semanticColors.info,
    gray: grayScale.light,
  },
  semanticTokens: {
    colors: {
      // Background colors
      'bg.surface': {
        default: 'white',
        _dark: 'gray.800',
      },
      'bg.canvas': {
        default: 'gray.50',
        _dark: 'gray.900',
      },
      'bg.subtle': {
        default: 'gray.100',
        _dark: 'gray.700',
      },
      
      // Text colors
      'text.primary': {
        default: 'gray.900',
        _dark: 'gray.50',
      },
      'text.secondary': {
        default: 'gray.600',
        _dark: 'gray.400',
      },
      'text.muted': {
        default: 'gray.500',
        _dark: 'gray.500',
      },
      
      // Border colors
      'border.default': {
        default: 'gray.200',
        _dark: 'gray.600',
      },
      'border.subtle': {
        default: 'gray.100',
        _dark: 'gray.700',
      },
    },
  },
  components: {
    Button: {
      variants: {
        solid: (props: any) => ({
          bg: `${props.colorScheme}.500`,
          color: 'white',
          _hover: {
            bg: `${props.colorScheme}.600`,
          },
          _active: {
            bg: `${props.colorScheme}.700`,
          },
        }),
        outline: (props: any) => ({
          borderColor: `${props.colorScheme}.500`,
          color: `${props.colorScheme}.500`,
          _hover: {
            bg: `${props.colorScheme}.50`,
          },
          _active: {
            bg: `${props.colorScheme}.100`,
          },
        }),
      },
    },
    Card: {
      baseStyle: {
        container: {
          bg: 'bg.surface',
          borderRadius: 'lg',
        },
      },
    },
  },
});

// Create Orchid theme
export const orchidTheme = extendTheme({
  config,
  colors: {
    primary: orchidColors.primary,
    secondary: orchidColors.secondary,
    accent: orchidColors.accent,
    success: semanticColors.success,
    warning: semanticColors.warning,
    error: semanticColors.error,
    info: semanticColors.info,
    gray: grayScale.light,
  },
  semanticTokens: {
    colors: {
      // Background colors
      'bg.surface': {
        default: 'white',
        _dark: 'gray.800',
      },
      'bg.canvas': {
        default: 'gray.50',
        _dark: 'gray.900',
      },
      'bg.subtle': {
        default: 'gray.100',
        _dark: 'gray.700',
      },
      
      // Text colors
      'text.primary': {
        default: 'gray.900',
        _dark: 'gray.50',
      },
      'text.secondary': {
        default: 'gray.600',
        _dark: 'gray.400',
      },
      'text.muted': {
        default: 'gray.500',
        _dark: 'gray.500',
      },
      
      // Border colors
      'border.default': {
        default: 'gray.200',
        _dark: 'gray.600',
      },
      'border.subtle': {
        default: 'gray.100',
        _dark: 'gray.700',
      },
    },
  },
  components: {
    Button: {
      variants: {
        solid: (props: any) => ({
          bg: `${props.colorScheme}.500`,
          color: 'white',
          _hover: {
            bg: `${props.colorScheme}.600`,
          },
          _active: {
            bg: `${props.colorScheme}.700`,
          },
        }),
        outline: (props: any) => ({
          borderColor: `${props.colorScheme}.500`,
          color: `${props.colorScheme}.500`,
          _hover: {
            bg: `${props.colorScheme}.50`,
          },
          _active: {
            bg: `${props.colorScheme}.100`,
          },
        }),
      },
    },
    Card: {
      baseStyle: {
        container: {
          bg: 'bg.surface',
          borderRadius: 'lg',
        },
      },
    },
  },
});

// Create Neon theme
export const neonTheme = extendTheme({
  config,
  colors: {
    primary: neonColors.primary,
    secondary: neonColors.secondary,
    accent: neonColors.accent,
    success: semanticColors.success,
    warning: semanticColors.warning,
    error: semanticColors.error,
    info: semanticColors.info,
    gray: grayScale.dark,
  },
  semanticTokens: {
    colors: {
      // Background colors with dark neon theme
      'bg.surface': {
        default: 'gray.900',
        _dark: 'gray.900',
      },
      'bg.canvas': {
        default: '#0a0a0a',
        _dark: '#0a0a0a',
      },
      'bg.subtle': {
        default: 'gray.800',
        _dark: 'gray.800',
      },
      
      // Text colors for dark neon theme
      'text.primary': {
        default: '#00ff41',
        _dark: '#00ff41',
      },
      'text.secondary': {
        default: '#2a95ff',
        _dark: '#2a95ff',
      },
      'text.muted': {
        default: 'gray.400',
        _dark: 'gray.400',
      },
      
      // Border colors with neon glow
      'border.default': {
        default: '#00ff41',
        _dark: '#00ff41',
      },
      'border.subtle': {
        default: 'gray.700',
        _dark: 'gray.700',
      },
    },
  },
  components: {
    Button: {
      variants: {
        solid: (props: any) => ({
          bg: `${props.colorScheme}.500`,
          color: 'gray.900',
          boxShadow: `0 0 10px ${props.colorScheme === 'primary' ? '#00ff41' : '#2a95ff'}`,
          _hover: {
            bg: `${props.colorScheme}.400`,
            boxShadow: `0 0 20px ${props.colorScheme === 'primary' ? '#00ff41' : '#2a95ff'}`,
          },
          _active: {
            bg: `${props.colorScheme}.600`,
          },
        }),
        outline: (props: any) => ({
          borderColor: `${props.colorScheme}.500`,
          color: `${props.colorScheme}.500`,
          boxShadow: `0 0 5px ${props.colorScheme === 'primary' ? '#00ff41' : '#2a95ff'}`,
          _hover: {
            bg: `${props.colorScheme}.900`,
            boxShadow: `0 0 15px ${props.colorScheme === 'primary' ? '#00ff41' : '#2a95ff'}`,
          },
          _active: {
            bg: `${props.colorScheme}.800`,
          },
        }),
      },
    },
    Card: {
      baseStyle: {
        container: {
          bg: 'bg.surface',
          borderRadius: 'lg',
          boxShadow: '0 0 10px rgba(0, 255, 65, 0.3)',
        },
      },
    },
  },
});

// Create Business theme
export const businessTheme = extendTheme({
  config,
  colors: {
    primary: businessColors.primary,
    secondary: businessColors.secondary,
    accent: businessColors.accent,
    success: semanticColors.success,
    warning: semanticColors.warning,
    error: semanticColors.error,
    info: semanticColors.info,
    gray: grayScale.light,
  },
  semanticTokens: {
    colors: {
      // Background colors
      'bg.surface': {
        default: 'white',
        _dark: 'gray.800',
      },
      'bg.canvas': {
        default: '#f8fafc',
        _dark: 'gray.900',
      },
      'bg.subtle': {
        default: 'gray.50',
        _dark: 'gray.700',
      },
      
      // Text colors
      'text.primary': {
        default: 'gray.900',
        _dark: 'gray.50',
      },
      'text.secondary': {
        default: 'gray.600',
        _dark: 'gray.400',
      },
      'text.muted': {
        default: 'gray.500',
        _dark: 'gray.500',
      },
      
      // Border colors
      'border.default': {
        default: 'gray.300',
        _dark: 'gray.600',
      },
      'border.subtle': {
        default: 'gray.200',
        _dark: 'gray.700',
      },
    },
  },
  components: {
    Button: {
      variants: {
        solid: (props: any) => ({
          bg: `${props.colorScheme}.500`,
          color: 'white',
          borderRadius: 'md',
          fontWeight: 'semibold',
          _hover: {
            bg: `${props.colorScheme}.600`,
          },
          _active: {
            bg: `${props.colorScheme}.700`,
          },
        }),
        outline: (props: any) => ({
          borderColor: `${props.colorScheme}.500`,
          color: `${props.colorScheme}.500`,
          borderRadius: 'md',
          fontWeight: 'semibold',
          _hover: {
            bg: `${props.colorScheme}.50`,
          },
          _active: {
            bg: `${props.colorScheme}.100`,
          },
        }),
      },
    },
    Card: {
      baseStyle: {
        container: {
          bg: 'bg.surface',
          borderRadius: 'md',
        },
      },
    },
  },
});

// Theme variants
export type ThemeVariant = 'ocean' | 'orchid' | 'neon' | 'business';

export const themes = {
  ocean: oceanTheme,
  orchid: orchidTheme,
  neon: neonTheme,
  business: businessTheme,
};

export default oceanTheme; 